import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  User, 
  Shield, 
  Activity, 
  Calendar,
  MessageSquare,
  BarChart3,
  Clock,
  TrendingUp
} from 'lucide-react';
import { UserData } from '@/services/admin/user-management-service';
import { formatDistanceToNow, format } from 'date-fns';

interface UserOverviewTabProps {
  user: UserData;
  onEditUser: () => void;
  onToggleUserStatus: (user: UserData) => void;
  onDeleteUser: (user: UserData) => void;
}

export const UserOverviewTab: React.FC<UserOverviewTabProps> = ({
  user,
  onEditUser,
  onToggleUserStatus,
  onDeleteUser,
}) => {
  const getStatusBadge = () => {
    if (user.isActive) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
          <Activity className="h-3 w-3 mr-1" />
          Active
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-gray-100 text-gray-800 border-gray-200">
        <Clock className="h-3 w-3 mr-1" />
        Inactive
      </Badge>
    );
  };

  const getSubscriptionBadge = () => {
    const plan = user.subscriptionPlan;
    const status = user.subscriptionStatus;
    
    if (plan === 'enterprise' && status === 'active') {
      return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Enterprise</Badge>;
    }
    if (plan === 'premium' && status === 'active') {
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Premium</Badge>;
    }
    return <Badge variant="secondary">Free</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* User Profile Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            User Profile Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            <Avatar className="h-20 w-20">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
                {user.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-3">
              <div>
                <h3 className="text-xl font-semibold">{user.name}</h3>
                <p className="text-muted-foreground">{user.email}</p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {getStatusBadge()}
                {getSubscriptionBadge()}
                {user.isAdmin && (
                  <Badge variant="destructive">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin
                  </Badge>
                )}
                {user.source === 'affiliate' && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    Referred by {user.affiliateName}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">AI Messages</p>
                <p className="text-2xl font-bold">{user.aiMessagesUsed.toLocaleString()}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Market Analysis</p>
                <p className="text-2xl font-bold">{user.marketAnalysisUsage.toLocaleString()}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Account Age</p>
                <p className="text-2xl font-bold">
                  {Math.floor((Date.now() - new Date(user.registrationDate).getTime()) / (1000 * 60 * 60 * 24))}d
                </p>
              </div>
              <Calendar className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Usage Score</p>
                <p className="text-2xl font-bold">{user.aiMessagesUsed + user.marketAnalysisUsage}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-muted-foreground">Registration Date</span>
                <span className="text-sm">{format(new Date(user.registrationDate), 'PPP')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-muted-foreground">Last Login</span>
                <span className="text-sm">
                  {user.lastLogin ? formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true }) : 'Never'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-muted-foreground">Subscription Plan</span>
                <span className="text-sm font-medium">{user.subscriptionPlan.charAt(0).toUpperCase() + user.subscriptionPlan.slice(1)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-muted-foreground">Account Status</span>
                <span className="text-sm">{user.subscriptionStatus.charAt(0).toUpperCase() + user.subscriptionStatus.slice(1)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Admin Actions</CardTitle>
            <CardDescription>
              Manage this user's account and permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                onClick={onEditUser}
                className="w-full justify-start"
              >
                <User className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
              
              <Button 
                variant={user.isActive ? "destructive" : "default"}
                onClick={() => onToggleUserStatus(user)}
                className="w-full justify-start"
              >
                <Activity className="h-4 w-4 mr-2" />
                {user.isActive ? 'Deactivate Account' : 'Activate Account'}
              </Button>
              
              <Button 
                variant="destructive" 
                onClick={() => onDeleteUser(user)}
                className="w-full justify-start"
              >
                <Shield className="h-4 w-4 mr-2" />
                Delete User
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};